/**
 * LRC Parser - Parse and sync lyrics in LRC format
 * Format: [mm:ss.xx] or [mm:ss] followed by lyric text
 */

export type LrcLine = {
  time: number; // seconds
  text: string;
};

const LRC_LINE_REGEX = /^\[(\d{1,2}):(\d{2})(?:\.(\d{2,3}))?\]\s*(.*)$/;

/**
 * Parse LRC text into array of { time, text }
 * Supports: [mm:ss.xx], [mm:ss], [m:ss]
 */
export function parseLrc(lrc: string | null | undefined): LrcLine[] {
  if (!lrc || typeof lrc !== "string") return [];

  const lines: LrcLine[] = [];
  const raw = lrc.trim().split(/\r?\n/);

  for (const line of raw) {
    const match = line.match(LRC_LINE_REGEX);
    if (!match) continue;

    const [, minStr, secStr, msStr, text] = match;
    const minutes = parseInt(minStr ?? "0", 10);
    const seconds = parseInt(secStr ?? "0", 10);
    const ms = msStr ? parseInt(msStr.padEnd(3, "0").slice(0, 3), 10) / 1000 : 0;

    const time = minutes * 60 + seconds + ms;
    if (text !== undefined && text.trim() !== "") {
      lines.push({ time, text: text.trim() });
    }
  }

  lines.sort((a, b) => a.time - b.time);
  return lines;
}

/**
 * Extract plain text lines from lyrics (for non-LRC content)
 */
export function parsePlainLyrics(lyrics: string | null | undefined): string[] {
  if (!lyrics || typeof lyrics !== "string") return [];
  return lyrics
    .trim()
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Get current line index and line for given currentTime
 */
export function getCurrentLine(
  lrcLines: LrcLine[],
  currentTime: number
): { index: number; line: LrcLine | null } {
  if (lrcLines.length === 0) return { index: -1, line: null };

  let index = -1;
  for (let i = 0; i < lrcLines.length; i++) {
    if (lrcLines[i].time <= currentTime) {
      index = i;
    } else {
      break;
    }
  }

  const line = index >= 0 ? lrcLines[index] : null;
  return { index, line };
}
